import React, { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import "./Blog.css";
import { useTranslation } from "react-i18next";

// Valeur interne stable, indépendante de la langue visible dans la liste.
const ALL_CATEGORIES = "__blog_all_categories__";

/**
 * Accent graphique uniquement : la chaîne i18n est conservée à l'identique.
 * Si le titre n'a pas cette terminaison, il est rendu intégralement tel quel.
 */
function BlogHeroHeading({ title }) {
  if (typeof title !== "string") return title;

  const match = /(maintenance\s+pr[ée]dictive|predictive\s+maintenance)(\s*)$/i.exec(title);
  if (!match) return title;

  return (
    <>
      {title.slice(0, match.index)}
      <span className="blog-hero__accent">{match[1]}</span>
      {match[2]}
    </>
  );
}

export default function Blog() {
  const { t, i18n } = useTranslation();

  // --------- Posts depuis i18n (plus de keys hardcodées) ----------
  const posts = useMemo(() => {
    const obj = t("blog.posts", { returnObjects: true }) || {};
    // obj = { key1: {slug,title,...}, key2: {...} }
    return Object.values(obj)
      .map((p) => {
        const tags = Array.isArray(p.tags) ? p.tags : [];
        return {
          slug: p.slug,
          title: p.title,
          excerpt: p.excerpt,
          image: p.image,
          date: p.date,
          author: p.author,
          category: p.category,
          tags,
          readTime: Number(p.readTime || 0),
        };
      })
      // tri du + récent au + ancien
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [t]);

  // --------- SEO basique sans dépendances ----------
  useEffect(() => {
    const description = t("blog.meta_description");

    // ✅ TITRE FIXE (ne change jamais)
    document.title = "UNITECH ENGINEERING";

    let desc = document.querySelector('meta[name="description"]');
    if (!desc) {
      desc = document.createElement("meta");
      desc.setAttribute("name", "description");
      document.head.appendChild(desc);
    }
    desc.setAttribute("content", description);

    const canonicalHref = `${window.location.origin}/blog`;
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", canonicalHref);

    // JSON-LD ItemList
    const base = `${window.location.origin}/blog`;
    const itemList = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListElement: posts.slice(0, 12).map((p, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `${base}/${p.slug}`,
        name: p.title,
      })),
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify(itemList);
    document.head.appendChild(script);

    return () => {
      if (script && document.head.contains(script)) document.head.removeChild(script);
    };
  }, [posts, t]);

  // --------- Recherche / Filtre ----------
  const [q, setQ] = useState("");
  const [cat, setCat] = useState(ALL_CATEGORIES);

  const categories = useMemo(() => {
    const uniq = Array.from(new Set(posts.map((p) => p.category)));
    return [ALL_CATEGORIES, ...uniq];
  }, [posts]);

  // Si une catégorie traduite n’existe plus dans la nouvelle langue,
  // revenir à « Toutes » sans modifier la recherche ni les articles.
  useEffect(() => {
    if (cat !== ALL_CATEGORIES && !categories.includes(cat)) {
      setCat(ALL_CATEGORIES);
    }
  }, [cat, categories]);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return posts.filter((p) => {
      const matchesCat = cat === ALL_CATEGORIES || p.category === cat;
      const hay = (p.title + " " + p.excerpt + " " + p.tags.join(" ")).toLowerCase();
      const matchesQ = hay.includes(qq);
      return matchesCat && matchesQ;
    });
  }, [posts, q, cat, t]);

  const locale = i18n.language === "fr" ? "fr-FR" : "en-US";

  return (
    <main className="blog" aria-labelledby="blog-title">
      <div className="blog-bg" aria-hidden="true" />

      <div className="blog-outer">
        <header className="blog-head">
          {/* Hero éditorial : texte i18n inchangé, présentation isolée. */}
          <div className="blog-hero">
            <h1 id="blog-title" className="blog-title">
              <BlogHeroHeading title={t("blog.title")} />
            </h1>
            <p className="blog-lede">{t("blog.lede")}</p>
          </div>

          <div className="blog-tools" role="search" aria-label={t("blog.tools_search_aria")}>
            <label className="sr-only" htmlFor="blog-search">
              {t("blog.search_label")}
            </label>
            <input
              id="blog-search"
              type="search"
              inputMode="search"
              placeholder={t("blog.search_placeholder")}
              className="blog-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />

            <label className="sr-only" htmlFor="blog-cat">
              {t("blog.category_label")}
            </label>
            <select
              id="blog-cat"
              className="blog-select"
              value={cat}
              onChange={(e) => setCat(e.target.value)}
            >
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c === ALL_CATEGORIES ? t("blog.filter_all") : c}
                </option>
              ))}
            </select>
          </div>
        </header>

        <section className="grid" aria-label={t("blog.grid_aria")}>
          {filtered.map((p) => (
            <article key={p.slug} className="card" itemScope itemType="https://schema.org/BlogPosting">
              <NavLink to={`/blog/${p.slug}`} className="card-media" aria-label={p.title}>
                <img src={p.image} alt="" loading="lazy" decoding="async" width="720" height="480" />
              </NavLink>

              <div className="card-body">
                <div className="meta">
                  <span className="chip">{p.category}</span>
                  <time dateTime={p.date}>
                    {new Date(p.date).toLocaleDateString(locale, {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </time>
                  <span aria-hidden="true">•</span>
                  <span>
                    {p.readTime} {t("blog.read_time_unit")}
                  </span>
                </div>

                <h2 className="card-title" itemProp="headline">
                  <NavLink to={`/blog/${p.slug}`} className="card-link">
                    {p.title}
                  </NavLink>
                </h2>

                <p className="card-excerpt" itemProp="description">
                  {p.excerpt}
                </p>

                <div className="card-footer">
                  <div className="author" itemProp="author">
                    {p.author}
                  </div>
                  <NavLink to={`/blog/${p.slug}`} className="readmore" aria-label={`${t("blog.read_more")} ${p.title}`}>
                    {t("blog.read_more")}
                  </NavLink>
                </div>
              </div>
            </article>
          ))}
        </section>

        <aside className="tags-wrap" aria-label={t("blog.popular_tags_aria")}>
          {(t("blog.popular_tags", { returnObjects: true }) || []).map((tag) => (
            <NavLink key={tag} to={`/blog/tag/${encodeURIComponent(tag)}`} className="tag">
              #{tag}
            </NavLink>
          ))}
        </aside>

        <div className="pager">
          <button type="button" className="btn-more" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
            {t("blog.see_more")}
          </button>
        </div>
      </div>
    </main>
  );
}
